import { format } from 'date-fns';
import type { ConversionRecord, DocumentCategory, ManualJournal, UploadedDocument } from '../types';
import { activeXeroConfirmation } from './xeroImport';

/**
 * The client's work, one row per item (docs/work-history.md §1): every journal, every upload no journal has used yet,
 * and every bank-flow file produced. Every status is DERIVED from fields the records already carry, never stored, so
 * there is no migration and no status that can drift from the record it describes.
 */
export const WORK_STATUSES = ['uploaded', 'draft', 'proposed', 'unconfirmed', 'confirmed', 'exported', 'corrected'] as const;
export type WorkStatus = (typeof WORK_STATUSES)[number];

export interface WorkItem {
  kind: 'journal' | 'upload' | 'conversion';
  id: string;
  status: WorkStatus;
  /** The source file names: a journal's cited uploads, the upload itself, or the file produced. */
  sources: string[];
  /** A journal's date, or a bank file's period; empty for an upload. */
  date: string;
  /** ISO timestamp of the latest thing that happened to it; the list is newest first by this. */
  lastActivity: string;
  who: string | null;
  /** A corrected journal: the journal that corrects it (B3), its own row. */
  correctedBy?: string;
  journal?: ManualJournal;
  upload?: UploadedDocument;
  conversion?: ConversionRecord;
}

/** Uploads that become a journal. A bank statement becomes a file (its conversion is the row); payroll is reference. */
const JOURNAL_SOURCES: readonly DocumentCategory[] = ['dr15Return', 'posReport'];

/**
 * Q1 (lean, a packet): "proposed" is a draft built from a template proposal; one typed by hand is "draft". A
 * correction (B3) outranks every other state: the journal it points to is the one that counts now.
 */
export function journalWorkStatus(journal: ManualJournal): WorkStatus {
  if (journal.supersededByJournalId) return 'corrected';
  if (journal.status === 'exported') return activeXeroConfirmation(journal) ? 'confirmed' : 'unconfirmed';
  return journal.templateId && journal.templateId !== 'blank' ? 'proposed' : 'draft';
}

function latest(...stamps: (string | null | undefined)[]): string {
  return stamps.reduce<string>((max, s) => (s && s > max ? s : max), '');
}

function journalActivity(journal: ManualJournal): string {
  const confirmations = (journal.xeroImportConfirmations ?? []).flatMap((c) => [c.confirmedAt, c.withdrawnAt]);
  return latest(journal.createdAt, journal.updatedAt, journal.exportedAt, ...confirmations);
}

export function deriveWorkItems({
  clientId,
  journals,
  uploads,
  conversions,
}: {
  /** When given, a record of any other client is dropped (the lists are per client already; this holds it). */
  clientId?: string;
  journals: ManualJournal[];
  /** Including removed ones: a journal still names a source that was removed later. */
  uploads: UploadedDocument[];
  conversions: ConversionRecord[];
}): WorkItem[] {
  const mine = <T extends { clientId: string }>(r: T) => !clientId || r.clientId === clientId;
  const liveJournals = journals.filter((j) => mine(j) && !j.isDeleted);
  const uploadName = new Map(uploads.filter(mine).map((u) => [u.id, u.fileName]));
  const cited = new Set(liveJournals.flatMap((j) => j.sourceDocumentIds ?? []));

  const items: WorkItem[] = [
    ...liveJournals.map((journal): WorkItem => ({
      kind: 'journal',
      id: journal.id,
      status: journalWorkStatus(journal),
      sources: (journal.sourceDocumentIds ?? []).flatMap((id) => uploadName.get(id) ?? []),
      date: journal.journalDate,
      lastActivity: journalActivity(journal),
      who: journal.createdByName ?? null,
      ...(journal.supersededByJournalId ? { correctedBy: journal.supersededByJournalId } : {}),
      journal,
    })),
    ...uploads
      .filter((u) => mine(u) && !u.isDeleted && JOURNAL_SOURCES.includes(u.category) && !cited.has(u.id))
      .map((upload): WorkItem => ({
        kind: 'upload',
        id: upload.id,
        status: 'uploaded',
        sources: [upload.fileName],
        date: '',
        lastActivity: upload.uploadedAt,
        who: upload.uploadedByName ?? null,
        upload,
      })),
    // Q2 (lean, a packet): bank-flow files are finished work, shown here as well as on Conversions. A journal's own
    // export record is NOT a second row: the journal's row already says it was exported.
    ...conversions
      .filter((c) => mine(c) && c.flow !== 'manualJournal')
      .map((conversion): WorkItem => ({
        kind: 'conversion',
        id: conversion.id,
        status: 'exported',
        sources: [conversion.fileName],
        date: conversion.periodStart && conversion.periodEnd ? `${conversion.periodStart} – ${conversion.periodEnd}` : '',
        lastActivity: conversion.generatedAt,
        who: conversion.generatedByName ?? null,
        conversion,
      })),
  ];
  return items.sort((a, b) => b.lastActivity.localeCompare(a.lastActivity));
}

export interface WorkFilters {
  status?: WorkStatus;
  /** `yyyy-MM-dd`, inclusive, on the day of last activity in the viewer's own zone. */
  from?: string;
  to?: string;
}

export function filterWorkItems(items: WorkItem[], filters: WorkFilters): WorkItem[] {
  return items.filter((item) => {
    if (filters.status && item.status !== filters.status) return false;
    const day = item.lastActivity ? format(new Date(item.lastActivity), 'yyyy-MM-dd') : '';
    if (filters.from && day < filters.from) return false;
    if (filters.to && day > filters.to) return false;
    return true;
  });
}

export function isWorkStatus(value: string | null): value is WorkStatus {
  return value !== null && (WORK_STATUSES as readonly string[]).includes(value);
}
